import { cn } from "@/lib/utils";

export interface ChipOption<T extends string> {
  value: T;
  label: string;
  /** Overrides the selected colours (e.g. a status pair). Defaults to solid ink. */
  activeClassName?: string;
}

const CHIP_BASE =
  "rounded-pill border px-4 py-[9px] text-sm font-bold leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
const CHIP_OFF = "border-waves-line bg-white text-waves-body hover:bg-waves-mist";
const CHIP_ON = "border-waves-ink bg-waves-ink text-white";

interface BaseProps<T extends string> {
  options: ChipOption<T>[];
  className?: string;
  disabled?: boolean;
  "aria-label"?: string;
}

type SingleProps<T extends string> = BaseProps<T> & {
  multiple?: false;
  value: T | null;
  onChange: (value: T) => void;
};

type MultiProps<T extends string> = BaseProps<T> & {
  multiple: true;
  value: T[];
  onChange: (value: T[]) => void;
};

// Pill-shaped toggle chips for picking one (status, prio) or several (disciplines)
// values. Selected chips are solid ink unless an option supplies its own colours.
export function ChipGroup<T extends string>(props: SingleProps<T> | MultiProps<T>) {
  const { options, className, disabled } = props;
  const isOn = (v: T) => (props.multiple ? props.value.includes(v) : props.value === v);

  const toggle = (v: T) => {
    if (props.multiple) {
      props.onChange(props.value.includes(v) ? props.value.filter((x) => x !== v) : [...props.value, v]);
    } else {
      props.onChange(v);
    }
  };

  return (
    <div role="group" aria-label={props["aria-label"]} className={cn("flex flex-wrap gap-2", className)}>
      {options.map((o) => {
        const on = isOn(o.value);
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => toggle(o.value)}
            className={cn(CHIP_BASE, on ? (o.activeClassName ?? CHIP_ON) : CHIP_OFF)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
